import React, { useEffect, useState, useCallback } from "react";
import { MainLayout } from "../MainLayout";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "../../lib/supabase_client";
import { useSession } from "../../contexts/SessionContext";
import '../../styles/page-base.css';

interface WalletTransaction {
  id: string;
  created_at: string;
  kind: 'topup' | 'bonus' | 'spend' | 'refund' | 'adjustment';
  amount_numeric: number;
  currency_balance: number;
  reference_type: string | null;
  reference_id: string | null;
  meta: any;
  created_by: string | null;
  action_by_name: string | null;
  description: string;
  credit_movement: number;
  balance_movement: number;
  closing_credit_balance: number;
  closing_balance: number;
}

export default function AdminOrganizationUsage() {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const { user } = useSession();
  const [transactions, setTransactions] = useState<WalletTransaction[]>([]);
  const [filingCompanyNames, setFilingCompanyNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [organizationName, setOrganizationName] = useState<string>('');
  const [countryIso, setCountryIso] = useState<string>('MY');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [transactionType, setTransactionType] = useState<string>('');

  const fetchOrganizationInfo = useCallback(async () => {
    if (!orgId) return;
    
    const { data, error } = await supabase
      .from('organizations')
      .select('name, country_iso')
      .eq('id', orgId)
      .maybeSingle();

    if (!error && data) {
      setOrganizationName(data.name);
      setCountryIso(data.country_iso || 'MY');
    }
  }, [orgId]);

  const fetchTransactions = useCallback(async () => {
    if (!orgId) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('usage-logs', {
        body: { org_id: orgId }
      });

      if (error) {
        console.error('Error fetching transactions:', error);
        setTransactions([]);
        return;
      }

      const fetchedTransactions: WalletTransaction[] = data?.transactions || [];
      setTransactions(fetchedTransactions);

      // ✅ Fetch company names for filing transactions
      const filingIds = fetchedTransactions
        .filter((tx: WalletTransaction) => tx.kind === 'spend' && 
                      tx.reference_type === 'filings' && 
                      tx.reference_id)
        .map((tx: WalletTransaction) => tx.reference_id!)
        .filter((id, index, self) => self.indexOf(id) === index); // Remove duplicates

      if (filingIds.length > 0) {
        const { data: filings, error: filingError } = await supabase
          .from('filings')
          .select('id, company_info')
          .in('id', filingIds);

        if (!filingError && filings) {
          const companyNameMap: Record<string, string> = {};
          filings.forEach((filing: any) => {
            // ✅ Add ALL fetched filings to map (even if no company name)
            // This lets us distinguish: filing exists vs filing deleted
            if (filing.company_info && typeof filing.company_info === 'object') {
              const companyName = filing.company_info.company_name;
              // Use empty string as marker for "filing exists but no company name"
              companyNameMap[filing.id] = companyName || '';
            } else {
              // No company_info at all - mark as empty
              companyNameMap[filing.id] = '';
            }
          });
          setFilingCompanyNames(companyNameMap);
        }
      } else {
        setFilingCompanyNames({});
      }
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    if (orgId) {
      fetchOrganizationInfo();
      fetchTransactions();
    }
  }, [orgId, fetchOrganizationInfo, fetchTransactions]);

  const isSingapore = countryIso === 'SG';

  const getDisplayedDescription = (transaction: WalletTransaction): string => {
    // Format description for upload filings
    if (transaction.kind === 'spend' && 
        transaction.reference_type === 'filings' && 
        transaction.description === 'Upload Filings' &&
        transaction.reference_id) {
      const filingId = transaction.reference_id;
      
      if (filingCompanyNames.hasOwnProperty(filingId)) {
        const companyName = filingCompanyNames[filingId];
        if (companyName && companyName.trim() !== '') {
          return companyName; // Case 1: Has company name
        } else {
          return 'Unnamed Company'; // Case 2: Exists but no name
        }
      } else {
        return 'Filing Deleted'; // Case 3: Filing not found
      }
    }
    // For topup and other transactions, show the description if available
    if (transaction.description && transaction.description !== '[description]') {
      return transaction.description;
    }
    // Fallback to reference_type
    return transaction.reference_type || '-';
  };

  // Filter transactions
  const filteredTransactions = transactions.filter(tx => {
    const displayedDesc = getDisplayedDescription(tx);
    const matchesSearch = !searchTerm || 
      displayedDesc.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tx.action_by_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (tx.reference_type && tx.reference_type.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesType = !transactionType || tx.kind === transactionType;
    
    return matchesSearch && matchesType;
  });

  // Pagination
  const totalPages = Math.ceil(filteredTransactions.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentTransactions = filteredTransactions.slice(startIndex, endIndex);

  const getNatureDisplay = (kind: string) => {
    switch (kind) {
      case 'topup':
        return 'topup';
      case 'bonus':
        return 'referral';
      case 'spend':
        return 'usage';
      case 'adjustment':
        return 'adjust';
      case 'refund':
        return 'refund';
      default:
        return kind;
    }
  };

  const getNatureBadgeStyle = (kind: string) => {
    switch (kind) {
      case 'spend':
        return 'bg-orange-200 text-orange-800 px-2 py-1 rounded text-xs font-semibold uppercase';
      case 'topup':
        return 'bg-blue-200 text-blue-800 px-2 py-1 rounded text-xs font-semibold uppercase';
      case 'bonus':
        return 'bg-purple-200 text-purple-800 px-2 py-1 rounded text-xs font-semibold uppercase';
      case 'refund':
        return 'bg-green-200 text-green-800 px-2 py-1 rounded text-xs font-semibold uppercase';
      case 'adjustment':
        return 'bg-yellow-200 text-yellow-800 px-2 py-1 rounded text-xs font-semibold uppercase';
      default:
        return 'bg-gray-200 text-gray-800 px-2 py-1 rounded text-xs font-semibold uppercase';
    }
  };

  const formatMovement = (amount: number) => {
    if (amount === 0) return '0';
    return amount > 0 ? `+${amount.toFixed(2)}` : amount.toFixed(2);
  };

  const formatBalance = (amount: number) => {
    return amount.toFixed(2);
  };

  const getMovementColor = (amount: number) => {
    if (amount === 0) return 'text-gray-500';
    return amount > 0 ? 'text-green-600' : 'text-red-600';
  };

  return (
    <MainLayout>
      <div className="page-container">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Header */}
          <div className="page-header">
            <div className="page-header-left">
              <div className="text-sm text-gray-500 mb-1">Admin Only</div>
              <h1 className="page-title">Usage Logs - {organizationName || 'Loading...'}</h1>
            </div>
            <div className="page-header-right">
              <button
                onClick={() => navigate('/admin/customers')}
                className="f-btn f-btn-primary"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                <span>Back to Customers</span>
              </button>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="page-card">
            <div className="flex items-center justify-between mb-6">
              <div className="relative flex-1 max-w-md">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="Search transactions..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="block w-full text-sm pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="ml-4">
                <select
                  value={transactionType}
                  onChange={(e) => {
                    setTransactionType(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="block w-full text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All Types</option>
                  <option value="topup">Topup</option>
                  <option value="bonus">Bonus</option>
                  <option value="spend">Spend</option>
                  <option value="refund">Refund</option>
                  <option value="adjustment">Adjustment</option>
                </select>
              </div>
            </div>

            {/* Table */}
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-x-auto">
              <table className="page-table">
                <thead className="page-table-thead">
                  <tr className="page-table-tr">
                    <th className="page-table-th">Date</th>
                    <th className="page-table-th">Nature</th>
                    <th className="page-table-th">Description</th>
                    {isSingapore && (
                      <th className="page-table-th">
                        <div className="text-center">Movement</div>
                        <div className="flex justify-center gap-4 mt-1 pt-1">
                          <div className="text-center text-xs font-normal">Credit</div>
                          <div className="text-center text-xs font-normal">SGD</div>
                        </div>
                      </th>
                    )}
                    {!isSingapore && (
                      <th className="page-table-th">
                        <div className="text-center">Movement</div>
                        <div className="text-center text-xs font-normal mt-1 pt-1">Credit</div>
                      </th>
                    )}
                    <th className="page-table-th">Action By</th>
                    {isSingapore && (
                      <th className="page-table-th">
                        <div className="text-center">Closing Balance</div>
                        <div className="flex justify-center gap-4 mt-1 pt-1">
                          <div className="text-center text-xs font-normal">Credit</div>
                          <div className="text-center text-xs font-normal">SGD</div>
                        </div>
                      </th>
                    )}
                    {!isSingapore && (
                      <th className="page-table-th">
                        <div className="text-center">Closing Balance</div>
                        <div className="text-center text-xs font-normal mt-1 pt-1">Credit</div>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="page-table-tbody">
                  {loading ? (
                    <tr className="page-table-tr">
                      <td colSpan={isSingapore ? 7 : 6} className="page-table-td text-center py-4 text-gray-500">
                        Loading...
                      </td>
                    </tr>
                  ) : currentTransactions.length === 0 ? (
                    <tr className="page-table-tr">
                      <td colSpan={isSingapore ? 7 : 6} className="page-table-td text-center py-4 text-gray-500">
                        No transactions found.
                      </td>
                    </tr>
                  ) : (
                    currentTransactions.map((transaction) => (
                      <tr key={transaction.id} className="page-table-tr">
                        <td className="page-table-td">
                          {(() => {
                            const dateObj = new Date(transaction.created_at);
                            const dateStr = dateObj.toLocaleDateString('en-GB');
                            const timeStr = dateObj.toLocaleTimeString('en-GB', { 
                              hour: '2-digit', 
                              minute: '2-digit',
                              hour12: true 
                            });
                            return (
                              <div className="flex flex-col">
                                <span>{dateStr}</span>
                                <span className="text-gray-500 text-xs">{timeStr}</span>
                              </div>
                            );
                          })()}
                        </td>
                        <td className="page-table-td">
                          <span className={getNatureBadgeStyle(transaction.kind)}>
                            {getNatureDisplay(transaction.kind)}
                          </span>
                        </td>
                        <td className="page-table-td">
                          {getDisplayedDescription(transaction)}
                        </td>
                        {isSingapore ? (
                          <td className="page-table-td text-center">
                            <span className="inline-block w-12">
                              <span className={getMovementColor(transaction.credit_movement)}>
                                {transaction.credit_movement === 0 ? '0' : formatMovement(transaction.credit_movement)}
                              </span>
                            </span>
                            <span className="inline-block w-12">
                              <span className={getMovementColor(transaction.balance_movement || 0)}>
                                {transaction.balance_movement === 0 ? '0' : formatMovement(transaction.balance_movement || 0)}
                              </span>
                            </span>
                          </td>
                        ) : (
                          <td className="page-table-td text-center">
                            <span className={getMovementColor(transaction.credit_movement)}>
                              {transaction.credit_movement === 0 ? '0' : formatMovement(transaction.credit_movement)}
                            </span>
                          </td>
                        )}
                        <td className="page-table-td">{transaction.action_by_name || 'System'}</td>
                        {isSingapore ? (
                          <td className="page-table-td text-center">
                            <span className="inline-block w-12">
                              <span>{formatBalance(transaction.closing_credit_balance)}</span>
                            </span>
                            <span className="inline-block w-12">
                              <span>{formatBalance(transaction.closing_balance || 0)}</span>
                            </span>
                          </td>
                        ) : (
                          <td className="page-table-td text-center">
                            {formatBalance(transaction.closing_credit_balance)}
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {!loading && filteredTransactions.length > 0 && (
              <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
                <div className="flex items-center space-x-4">
                  <span className="text-sm text-gray-600">Rows per page:</span>
                  <select
                    value={rowsPerPage}
                    onChange={(e) => {
                      setRowsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="border border-gray-300 rounded px-2 py-1"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <span>
                    {startIndex + 1}-{Math.min(endIndex, filteredTransactions.length)} of {filteredTransactions.length}
                  </span>
                </div>
                <div className="flex space-x-1">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                    disabled={currentPage === 1}
                    className="f-btn f-btn-soft f-btn-xs disabled:opacity-40"
                  >
                    &lt;
                  </button>
                  <button className="f-btn f-btn-primary f-btn-xs">
                    {currentPage}
                  </button>
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="f-btn f-btn-soft f-btn-xs disabled:opacity-40"
                  >
                    &gt;
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
}